<template>
    <teleport v-if="isTeleportReady && showSidePanel && width > 1400" to="#side-panel">
        <Transition name="side-panel-fade" appear>
            <div v-if="panelVisible" class="right-side-context-menu">
                <div
                    v-if="route.name === 'workflow' && showRunContextPill"
                    class="workflow-run-context-pill"
                >
                    <div class="workflow-run-context-pill__status" :class="`is-${connectionStatusColor}`">
                        <v-icon size="16">{{ connectionStatusIcon }}</v-icon>
                        <span>{{ connectionStatusText }}</span>
                    </div>
                    <span class="workflow-run-context-pill__separator" aria-hidden="true">|</span>
                    <button type="button" class="workflow-run-context-pill__action" @click="$emit('exitRunContext')">
                        <span>{{ runContextExitLabel }}</span>
                    </button>
                </div>
                 <v-col v-if="route.name === 'agent'">
                    <agent-side-panel
                        @load-agent="(agent) => $emit('loadAgent', agent)"
                        @edit-agent="(agent) => $emit('editAgent', agent)"
                        @delete-agent="(agent) => $emit('deleteAgent', agent)"
                    />
                </v-col>
                <v-col v-if="route.name === 'workflow'">
                    <div class="workflow-side-panel-stack">
                        <workflow-status-panel
                            :current-run="workflowStore.currentRun"
                            :workflow-name="workflowStore.currentWorkflow?.name || ''"
                            :event-count="workflowStore.events.length"
                        />
                    </div>
                </v-col>
            </div>
        </Transition>
    </teleport>
</template>

<script lang="ts">
import type {Ref} from "vue"
import {computed, defineComponent, inject, nextTick, onMounted, onUnmounted, ref, watch} from "vue"
import StatusIndicator from "@/components/StatusIndicator.vue";
import Panel from "@/components/generics/Panel/Panel.vue";
import ListItem from "@/components/generics/ListItem/ListItem.vue";
import AgentSidePanel from "@/components/panels/AgentSidePanel.vue";
import {useWindowSize} from "@vueuse/core";
import WorkflowStatusPanel from "@/components/panels/WorkflowStatusPanel.vue";
import {useRoute} from "vue-router";
import {useWorkflowStore} from "@/stores/WorkflowStore/WorkflowStore";

export default defineComponent({
    props: {
        visible: {
            type: Boolean,
            default: true,
        },
        connectionStatusColor: {
            type: String,
            default: 'grey',
        },
        connectionStatusIcon: {
            type: String,
            default: 'mdi-circle-outline',
        },
        connectionStatusText: {
            type: String,
            default: '',
        },
        showRunContextPill: {
            type: Boolean,
            default: false,
        },
        runContextExitLabel: {
            type: String,
            default: 'Exit view',
        },
    },
    components: {
        AgentSidePanel,
        WorkflowStatusPanel,
        ListItem,
        Panel,
        StatusIndicator
    },
    emits: ['loadAgent', 'editAgent', 'deleteAgent', 'exitRunContext'],
    setup(props) {
        const route = useRoute();
        const {width} = useWindowSize();
        const workflowStore = useWorkflowStore();

        const showSidePanel: Ref = inject('showSidePanel') ?? ref<boolean>(false)
        const isTeleportReady = ref(false)
        const panelVisible = computed(() => props.visible)

        const syncSidePanelState = async (visible: boolean) => {
            if (visible) {
                showSidePanel.value = true
                await nextTick()
                isTeleportReady.value = true
                return
            }

            isTeleportReady.value = false
            showSidePanel.value = false
        }

        onMounted(() => {
            void syncSidePanelState(props.visible)
        })

        watch(() => props.visible, (visible) => {
            void syncSidePanelState(visible)
        })

        onUnmounted(() => {
            // Disable teleport BEFORE KSidePanel can remove #side-panel from the DOM.
            isTeleportReady.value = false
            showSidePanel.value = false
        })


        return {
            // computed
            width,
            // ref
            route,
            showSidePanel,
            isTeleportReady,
            panelVisible,
            workflowStore,
        }
    },
})
</script>

<style lang="scss" scoped>
.v-expansion-panel-text::v-deep(.v-expansion-panel-text__wrapper) {
    padding-right: 0 !important;
}

.fixed-card {
    left: -60px;
    width: 300px;

}

.fixed-card-2 {
    position: fixed;
    top: 430px;
    left: -60px;
    z-index: 1000;
    width: 300px;
}

.side-panel-fade-enter-active,
.side-panel-fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.side-panel-fade-enter-from,
.side-panel-fade-leave-to {
    opacity: 0;
    transform: translateX(10px);
}

.right-side-context-menu {
    position: relative;
    min-height: calc(100vh - 124px);
    padding-bottom: 72px;
}

.workflow-side-panel-stack {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.workflow-run-context-pill {
    position: absolute;
    left: 50%;
    bottom: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 42px;
    transform: translateX(-50%);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid rgba(218, 224, 232, 0.96);
    box-shadow: 0 12px 26px rgba(28, 42, 56, 0.12), 0 2px 6px rgba(28, 42, 56, 0.08);
    backdrop-filter: blur(10px);
    overflow: hidden;
    z-index: 2;
}

.workflow-run-context-pill__status,
.workflow-run-context-pill__action {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 42px;
    padding: 0 12px;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
}

.workflow-run-context-pill__status {
    color: #667789;
}

.workflow-run-context-pill__separator {
    color: #a4b0bc;
    font-size: 13px;
    font-weight: 700;
    line-height: 1;
}

.workflow-run-context-pill__status.is-success {
    color: #2e8b57;
}

.workflow-run-context-pill__status.is-error {
    color: #c24d4d;
}

.workflow-run-context-pill__action {
    border: 0;
    background: transparent;
    color: #1976d2;
    cursor: pointer;
    transition: background-color 0.16s ease, color 0.16s ease;
    border-radius: 999px;
}

.workflow-run-context-pill__action:hover {
    color: #ffffff;
}
</style>
